import { find, get, isArray, isEmpty } from 'lodash'
import logger from './logger'
import { isActive, isSuspended, isVisible } from './status'
import { BET_EVENT_TYPE } from '../constants'
import { isExoticMarket } from './market'
import { isMultilegsMarket } from '../index'
import { BETSLIP_ERROR_CODE } from '../error'

/*
  Helper function that returns bet event partials
  Used in betslip bets
*/

export function getBetEventPartials (event, options) {
  const {
    marketTypeId,
    marketId,
    outcomeId,
    betEventType,
    isBespokeOutcome
  } = options

  let marketType
  let market
  let runner
  let outcome
  let odds
  let oddsForMulti
  let suspended
  let active

  switch (betEventType) {
    case BET_EVENT_TYPE.RACING:
      market = (event.markets || []).find(m => m.marketId === marketId) || {}
      marketType = market?.type || {}
      outcome = (market?.outcomes || []).find(o => o.outcomeId === outcomeId) || {}
      odds = outcome?.odds
      runner = (event.runners || []).find(r => r.number.toString() === outcome?.selection || r.number.toString() === outcome?.specifier) || {}
      suspended = event?.status !== 'OPEN' || isSuspended(market) || runner?.scratched === true
      active = event?.status === 'OPEN' && (market?.status === 'NORMAL' || !!isBespokeOutcome)
      break

    case BET_EVENT_TYPE.SPORT_PREMATCH:
    case BET_EVENT_TYPE.SPORT_LIVE:
    default:
      marketType = get(event, `marketTypes.${marketTypeId}`, {})
      market = get(marketType, `markets.${marketId}`, {})
      outcome = get(market, `outcomes.${outcomeId}`, {})
      odds = outcome.odds
      oddsForMulti = outcome.oddsForMulti
      suspended = isSuspended(event) || isSuspended(market) || isSuspended(outcome)
      active = (isActive(event) && isActive(market) && isActive(outcome)) || (!!isBespokeOutcome && isActive(event))
      break
  }

  return {
    marketType,
    market,
    runner,
    outcome,
    odds,
    oddsForMulti,
    suspended,
    active
  }
}

/*
  Method for extracting violations by outcomeIds from bet error
*/
const UNSUPPORTED_ERROR_CODES = [
  BETSLIP_ERROR_CODE.MAX_BET_AMOUNT_EXCEEDED,
  BETSLIP_ERROR_CODE.MIN_BET_AMOUNT_EXCEEDED,
  BETSLIP_ERROR_CODE.NOT_VALID_AMOUNT
]
export function extractBestlipOutcomesErrors (errors) {
  const outcomeIdsErrors = {}

  for (const error of Object.values(errors)) {
    if (isEmpty(error.violations)) {
      continue
    }

    for (const violation of error.violations) {
      const {
        code,
        violationInfo
      } = violation

      // Skip if violation info not provided
      if (!violationInfo || UNSUPPORTED_ERROR_CODES.includes(code)) {
        continue
      }

      if (violationInfo.outcomeId) {
        outcomeIdsErrors[violationInfo.outcomeId] = violation
        continue
      }

      if (violationInfo.outcomeIds) {
        violationInfo.outcomeIds.forEach((outcomeId) => {
          outcomeIdsErrors[outcomeId] = violation
        })
      }
    }
  }

  return outcomeIdsErrors
}

export function getBetsHash (bets) {
  return bets
    .map(bet => bet.outcomeId)
    .join(';')
}

/*
  Used in broadcasting new bet items snapshot

  Reacting on outcome id changes & amout changes
*/
export function isBetsDifferent (targetBets, sourceBets) {
  if (targetBets.length !== sourceBets.length) {
    return true
  }

  // If lengths are equal
  for (const targetIndex in targetBets) {
    const target = targetBets[targetIndex]
    const source = sourceBets[targetIndex]

    // If outcome id not the same
    if ((target.outcomeId !== source.outcomeId) && (target.odds !== source.odds)) {
      return true // Bets are different
    }
  }

  // Bets are equal
  return false
}

export function extractBetsFromBetDtoOutcomes (outcomes) {
  // Validating argument
  if (!Array.isArray(outcomes) || isEmpty(outcomes)) {
    return []
  }

  const betItems = []

  for (const outcome of outcomes) {
    const eventId = String(get(outcome, 'outcomeInfo.eventId'))

    const marketTypeId = get(outcome, 'outcomeInfo.marketTypeId')

    const betEventType = get(outcome, 'outcomeInfo.sportService')

    const marketId = String(get(outcome, 'outcomeInfo.marketId'))

    const outcomeId = String(get(outcome, 'outcomeId'))

    const betGroups = get(outcome, 'outcomeInfo.betMarketGroups')

    // Verifying that required fields are included
    if (eventId && marketTypeId && marketId && outcomeId) {
      betItems.push({
        eventId,
        marketTypeId,
        marketId,
        outcomeId,
        betGroups,
        betEventType
      })
    }
  }

  return betItems
}

/*
  Converting fetched events for betslip bet items
  With validations

  @param {Event[]} events
  @param {StoredBetItem[]} betsIds
  @example betsIds [
    {
      eventId: 1
      marketId: 22
      outcomeId: 333
    }
  ]
*/
export function convertFetchedEvents (events, betsIds) {
  return betsIds.reduce(
    (acc, betIds) => {
      const {
        eventId,
        marketId,
        marketTypeId,
        outcomeId
      } = betIds

      try {
        const event = find(events, { id: eventId })

        if (!event) {
          throw new Error(`event[${eventId}] not found`)
        }

        const marketType = get(event, `marketTypes[${marketTypeId}]`)

        if (!marketType) {
          throw new Error(`marketType[${marketTypeId}] not found`)
        }

        const market = get(marketType, `markets[${marketId}]`)

        if (!market) {
          throw new Error(`market[${marketId}] not found`)
        }

        const outcome = get(market, `outcomes.${outcomeId}`)

        if (!outcome) {
          throw new Error(`outcome[${outcomeId}] not found`)
        }

        return [
          ...acc,
          {
            event,
            marketTypeId,
            marketId,
            outcomeId
          }
        ]
      } catch (error) {
        logger.debug(
          '[betstorage]',
          `unable to resolve event[${eventId}] data:`,
          error.message
        )
        return acc
      }
    },
    []
  )
}

/*
  Helper method for extracting bonuses from validation response
  Need after success validation (using in BetManager)
*/
export function extractBonuses (allowedBonuses) {
  const bonuses = {}

  if (isEmpty(allowedBonuses) || !isArray(allowedBonuses)) {
    return bonuses
  }

  for (const bonus of allowedBonuses) {
    const {
      betRequestId,
      allowedBonuses
    } = bonus

    if (isEmpty(allowedBonuses)) {
      continue
    }

    bonuses[betRequestId] = allowedBonuses
  }

  return bonuses
}

/*
  Counting bet items with ordered odds
*/
export function recapOrderedBetItems (betItems) {
  return betItems.reduce((acc, betItem) => {
    // Using ordered odds
    const betItemOdd = Number.parseFloat((betItem.orderOdds / 100)) || 1
    const betItemAmount = Number.parseFloat(betItem.amount) || 0

    return {
      ...acc,
      total: acc.total + betItemAmount,
      return: acc.return + (betItemOdd * betItemAmount)
    }
  }, {
    total: 0,
    return: 0
  })
}

/*
  Counting bet items total and return
*/
export function recapBetsTotal (betItems) {
  return betItems.reduce((acc, bet) => {
    const betItemOdds = Number.parseFloat(bet.odds) || 1
    const betItemAmount = Number.parseFloat(bet.amount) || 0

    return {
      ...acc,
      total: acc.total + betItemAmount,
      return: acc.return + (betItemOdds * betItemAmount)
    }
  }, {
    total: 0,
    return: 0
  })
}

/*
  Helper function to get bet outcome
  For future bet structure upgrades
*/
export function getBetOutcome (bet) {
  const {
    event,
    marketTypeId,
    marketId,
    outcomeId
  } = bet

  return get(
    event,
    `marketTypes.${marketTypeId}.markets.${marketId}.outcomes.${outcomeId}`,
    {}
  )
}

export function getBetMarket (bet) {
  const {
    event,
    marketTypeId,
    marketId
  } = bet

  return get(
    event,
    `marketTypes.${marketTypeId}.markets.${marketId}`,
    {}
  )
}

/*
  Helper function to get bet market type
  For future bet structure upgrades
*/
export function getBetMarketType (bet) {
  const {
    event,
    marketTypeId
  } = bet

  return get(
    event,
    `marketTypes.${marketTypeId}`,
    {}
  )
}

// Creating verifier that will fail if isActive(bet) failed

// If Bet active you can place bet
// Active means SUSPENDED OR ACTIVE NOT HIDDEN
// For this case we using isVisible() check
export const isBetActive = (bet) => {
  const {
    betEventType,
    event,
    marketTypeId,
    marketId,
    outcomeId,
    isBespokeOutcome
  } = bet

  /*
    Check event if it exists
    Do event verify
    Check if next step can be applyed
  */
  if (isEmpty(event) === true || isVisible(event) === false) {
    return false
  }

  if (isBespokeOutcome && marketTypeId && marketId && outcomeId) {
    // event doesn't contain bespoke markets inside, so just check ids and hope
    return true
  }

  const market = betEventType === BET_EVENT_TYPE.RACING
    ? (event.markets || []).find(m => m.marketId === marketId)
    : event.marketTypes[marketTypeId]?.markets[marketId]

  // Checking market and market outcomes
  if (isEmpty(market) === true || isVisible(market) === false || isEmpty(market.outcomes) === true) {
    return false
  }

  const outcome = betEventType === BET_EVENT_TYPE.RACING
    ? (market.outcomes || []).find(o => o.outcomeId === outcomeId)
    : market.outcomes[outcomeId]

  if (isEmpty(outcome) === true || isVisible(outcome) === false) {
    return false
  }

  return true
}

// Creating verifier that will failed if isSuspended(bet) failed
export const isBetSuspended = (bet) => {
  const {
    event,
    marketTypeId,
    marketId,
    outcomeId
  } = bet

  /*
    Check event if it exists
    Do event verify
    Check if next step can be applyed
  */
  if (isEmpty(event)) { return false }
  if (isSuspended(event)) { return true }
  if (isEmpty(event.marketTypes)) { return false }

  // Picking up market type
  const marketType = event.marketTypes[marketTypeId]

  // Check market type and its markets
  if (isEmpty(marketType) || isEmpty(marketType.markets)) { return false }

  // Picking market
  const market = marketType.markets[marketId]

  // Verification of market (same as event)
  if (isEmpty(market)) { return false }
  if (isSuspended(market)) { return true }
  if (isEmpty(market.outcomes)) { return false }

  const outcome = market.outcomes[outcomeId]

  // Verification of outcome
  if (isEmpty(outcome)) {
    return false
  }

  // If we can get to outcome and all statuses verified
  return false
}

/*
  Helper function for validating bet groups
  Should be not empty array
*/
export const isBetGroupsValid = (betGroups) => (
  Array.isArray(betGroups) && (betGroups.length > 0)
)

/*
  Helper function for checking are bet groups are not intersected with each other
*/
export const areBetGroupsDisintersected = (targetBetGroups, sourceBetGroups) => {
  // Check if bet groups are valid
  if (isBetGroupsValid(targetBetGroups) && isBetGroupsValid(sourceBetGroups)) {
    // Looking for bet groups intersections
    return !targetBetGroups.some(
      betGroup => sourceBetGroups.includes(betGroup)
    )
  }

  // Otherwise groups are not intersected
  return false
}

export const getBetItemsForMulti = (betItems) => betItems.filter((bet) => {
  if (bet.onlySingle === true) {
    return false
  }
  if (isExoticMarket(bet.marketTypeId) || isMultilegsMarket(bet.marketTypeId)) {
    return false
  }
  return true
})

export const getBonusAssignedToBet = ({ requestId, bonusAssignments, bonuses }) => {
  const bonusAssignment = (bonusAssignments || []).find((it) => it.requestId === requestId)
  return bonusAssignment
    ? ((bonuses || {})[requestId] || []).find((bonus) => bonus.id === bonusAssignment.bonusId)
    : null
}
